/**
 * Screen-size aware LOD for overlay equipment / stockers.
 * Active transfer stations always force near detail so pick/drop stays accurate.
 */
/* eslint-disable react-refresh/only-export-components -- shared LOD math and hook intentionally colocated */
import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { cameraDragStore } from '../../store/cameraDragStore';
import { liveOhtRuntimeStore } from '../../store/liveOhtRuntimeStore';
import { transferEquipmentDetailHoldStore } from '../../store/transferEquipmentDetailHoldStore';
import { useAppSelector } from '../../hooks/redux';

export type EquipmentLodLevel = 'near' | 'far';

/**
 * Thresholds are the machine's projected size in CSS pixels, so detail follows
 * how large it actually looks instead of how far it sits from the camera on the
 * ground plane. The gap between the two is hysteresis, which stops a tool
 * flickering between kit and box while the camera drifts across the boundary.
 */
const NEAR_PX = 96;
const FAR_PX = 64;
/** Used when the camera is not perspective, where pixel projection does not apply. */
const FALLBACK_NEAR_DIST = 220;
const FALLBACK_FAR_DIST = 320;
const CHECK_INTERVAL_S = 0.2;
/** Max kit promotions/demotions per LOD tick — spreads shader compile across frames. */
export const EQUIPMENT_DETAIL_GRADUAL_STEP = 1;

/**
 * Promote to full kits immediately when the camera or selection asks for them;
 * demote at most one id per step to spread GPU work when zooming out.
 */
export function stepEquipmentDetailIds(
  current: readonly string[],
  target: readonly string[],
  forceIds?: ReadonlySet<string>,
  maxStep = EQUIPMENT_DETAIL_GRADUAL_STEP,
  maxPromote = Number.POSITIVE_INFINITY
): string[] {
  const targetSet = new Set(target);
  let next = current.filter((id) => targetSet.has(id) || forceIds?.has(id));

  // Forced IDs in target must be promoted immediately without using the gradual promote budget
  if (forceIds) {
    for (const id of target) {
      if (forceIds.has(id) && !next.includes(id)) {
        next.push(id);
      }
    }
  }

  let added = 0;
  for (const id of target) {
    if (!next.includes(id)) {
      if (added < maxPromote) {
        next.push(id);
        added += 1;
      }
    }
  }

  let removed = 0;
  for (const id of current) {
    if (removed >= maxStep) break;
    if (targetSet.has(id)) continue;
    if (forceIds?.has(id)) continue;
    if (!next.includes(id)) continue;
    next = next.filter((x) => x !== id);
    removed += 1;
  }

  return next;
}

type ActiveTransferRef = { stationId?: string; portId?: string };
const activeTransfersByFrame = new WeakMap<object, ActiveTransferRef[]>();

export function stationHasActiveTransfer(elementId: string): boolean {
  const frame = liveOhtRuntimeStore.getFrame();
  let transfers = activeTransfersByFrame.get(frame);
  if (!transfers) {
    transfers = Object.values(frame.byOhtId).flatMap((runtime) =>
      runtime.transfer ? [runtime.transfer] : []
    );
    activeTransfersByFrame.set(frame, transfers);
  }
  for (const transfer of transfers) {
    if (transfer.stationId === elementId) return true;
    if (transfer.portId?.startsWith(`${elementId}-`)) return true;
    if (transfer.portId === elementId) return true;
  }
  return false;
}

function activeTransferKey(): string {
  const frame = liveOhtRuntimeStore.getFrame();
  const values: string[] = [];
  for (const runtime of Object.values(frame.byOhtId)) {
    const transfer = runtime.transfer;
    if (!transfer) continue;
    values.push(`${transfer.stationId ?? ''}\u0001${transfer.portId ?? ''}`);
  }
  return values.sort().join('\u0002');
}

/**
 * Stable dynamic-model selection. Unlike camera LOD, this changes only when
 * a live FOUP pick/drop transfer starts or ends — orbit/zoom never swaps models.
 */
export function useDynamicEquipmentIds(candidateIds: readonly string[]): Set<string> {
  const running = useAppSelector((s) => s.simulation.running);
  const transferKey = useSyncExternalStore(
    (notify) => liveOhtRuntimeStore.subscribe(notify),
    activeTransferKey,
    () => ''
  );
  const holdVersion = useSyncExternalStore(
    (notify) => transferEquipmentDetailHoldStore.subscribe(notify),
    () => transferEquipmentDetailHoldStore.getVersion(),
    () => 0
  );
  return useMemo(
    () => collectDynamicEquipmentIds(candidateIds, transferKey, running, holdVersion),
    [candidateIds, running, transferKey, holdVersion]
  );
}

/**
 * Full procedural kits are limited to the selection and live handoffs.
 * Orbit and pan keep the instanced fleet so the camera can move every frame.
 */
export function selectPerformanceDetailIds(
  forceIds: ReadonlySet<string>,
  transferIds: ReadonlySet<string>,
  maxDetailed: number
): Set<string> {
  const next = new Set<string>();
  const cap = Math.max(0, maxDetailed);
  for (const id of forceIds) {
    if (next.size >= cap) return next;
    next.add(id);
  }
  for (const id of transferIds) {
    if (next.size >= cap) return next;
    next.add(id);
  }
  return next;
}

/** Full kit only while a FOUP handoff needs accurate port geometry. */
export function shouldUseFullEquipmentKit(
  _kind: 'equipment' | 'stocker',
  isTransferActive: boolean
): boolean {
  return isTransferActive;
}

export function collectDynamicEquipmentIds(
  candidateIds: readonly string[],
  transferKey: string,
  running: boolean,
  _holdVersion = 0
): Set<string> {
  const ids = new Set<string>();
  if (!running) return ids;
  if (transferKey) {
    const transfers = transferKey.split('\u0002').map((entry) => {
      const [stationId = '', portId = ''] = entry.split('\u0001');
      return { stationId, portId };
    });
    for (const id of candidateIds) {
      if (
        transfers.some(
          (transfer) =>
            transfer.stationId === id ||
            transfer.portId === id ||
            transfer.portId.startsWith(`${id}-`)
        )
      ) {
        ids.add(id);
      }
    }
  }
  const nowMs = performance.now();
  for (const id of candidateIds) {
    if (transferEquipmentDetailHoldStore.isHeld(id, nowMs)) ids.add(id);
  }
  return ids;
}

export type EquipmentLodCandidate = {
  id: string;
  worldX: number;
  worldY: number;
  worldZ: number;
  worldSize: number;
};

/**
 * One frame subscriber selects detail for an entire layer. All non-selected
 * machines remain present through the recognizable instanced renderer.
 */
export function useEquipmentDetailIds(
  candidates: EquipmentLodCandidate[],
  options: {
    maxDetailed?: number;
    nearPx?: number;
    farPx?: number;
    forceIds?: ReadonlySet<string>;
    /** When false, only selection / transfer stations use full kits (live sim perf). */
    promoteByCamera?: boolean;
  } = {}
): Set<string> {
  const running = useAppSelector((s) => s.simulation.running);
  const promoteByCamera = options.promoteByCamera ?? true;
  const forceIdsKey = options.forceIds ? [...options.forceIds].sort().join('|') : '';
  const [ids, setIds] = useState<string[]>(() => {
    const list: string[] = [];
    if (options.forceIds) {
      for (const id of options.forceIds) {
        list.push(id);
      }
    }
    return list;
  });
  const keyRef = useRef(ids.slice().sort().join('|'));
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const accumRef = useRef(0);
  const previousRef = useRef(new Set(ids));
  const maxDetailed = options.maxDetailed ?? Number.POSITIVE_INFINITY;
  // `options.nearPx` / `options.farPx` are accepted but not yet consumed by this
  // hook; the pixel thresholds are applied in <EquipmentLodRoot /> below.

  useEffect(() => {
    if (promoteByCamera) return;
    const forced: string[] = [];
    const seen = new Set<string>();
    const push = (id: string) => {
      if (seen.has(id)) return;
      seen.add(id);
      forced.push(id);
    };
    if (options.forceIds) {
      for (const id of options.forceIds) push(id);
    }
    if (running) {
      for (const candidate of candidates) {
        if (stationHasActiveTransfer(candidate.id)) push(candidate.id);
      }
    }
    const key = forced.slice().sort().join('|');
    if (key === keyRef.current) return;
    keyRef.current = key;
    previousRef.current = new Set(forced);
    setIds(forced);
  }, [candidates, forceIdsKey, promoteByCamera, running, options.forceIds]);

  const lastCamPos = useRef(new THREE.Vector3());
  const lastCamRot = useRef(new THREE.Quaternion());
  const cameraSettledRef = useRef(0);
  const projScreenMatrixRef = useRef(new THREE.Matrix4());
  const frustumRef = useRef(new THREE.Frustum());
  const sphereRef = useRef(new THREE.Sphere());

  useFrame((state, dt) => {
    if (!promoteByCamera) return;
    accumRef.current += dt;

    const { camera, size } = state;
    const posDelta = camera.position.distanceTo(lastCamPos.current);
    const rotDelta = camera.quaternion.angleTo(lastCamRot.current);
    lastCamPos.current.copy(camera.position);
    lastCamRot.current.copy(camera.quaternion);

    const hasPendingForced = candidates.some(
      (c) =>
        (options.forceIds?.has(c.id) === true || (running && stationHasActiveTransfer(c.id))) &&
        !idsRef.current.includes(c.id)
    );

    // Defer large batches during rapid camera motion, but keep forced and progressive updates going
    const isMoving = posDelta > 0.08 || rotDelta > 0.005;
    if (isMoving) {
      cameraDragStore.notifyCameraMotion();
      cameraSettledRef.current = 0;
    } else {
      cameraSettledRef.current += dt;
    }

    // Throttle check interval: 0.08s when settled, 0.16s while moving
    const checkInterval = isMoving ? 0.16 : 0.08;
    if (!hasPendingForced && accumRef.current < checkInterval) return;
    accumRef.current = 0;

    // Calculate Three.js camera frustum for visibility prioritization
    projScreenMatrixRef.current.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    frustumRef.current.setFromProjectionMatrix(projScreenMatrixRef.current);

    const scored: { id: string; score: number; forced: boolean }[] = [];
    for (const candidate of candidates) {
      const forced =
        options.forceIds?.has(candidate.id) === true ||
        (running && stationHasActiveTransfer(candidate.id));

      sphereRef.current.center.set(candidate.worldX, candidate.worldY, candidate.worldZ);
      sphereRef.current.radius = Math.max(candidate.worldSize * 0.7, 4.0);
      const inFrustum = frustumRef.current.intersectsSphere(sphereRef.current);

      let score = -Number.MAX_VALUE;

      if (forced) {
        // Priority 1: Selected equipment or active transfer -> real model immediately
        score = 1e9;
      } else if (inFrustum) {
        // Priority 2 & 3: Visible equipment
        const px =
          camera instanceof THREE.PerspectiveCamera
            ? projectedPixelSize(
              camera,
              size.height,
              candidate.worldX,
              candidate.worldY,
              candidate.worldZ,
              candidate.worldSize
            )
            : (size.height / Math.max(camera.top - camera.bottom, 1)) * candidate.worldSize;

        // Visible nearby equipment gets higher score (px), visible far gets positive score
        score = Math.max(1, px);
      } else {
        // Priority 4: Outside camera -> lowest priority (negative score)
        const dx = camera.position.x - candidate.worldX;
        const dz = camera.position.z - candidate.worldZ;
        score = -10000 - Math.hypot(dx, dz);
      }
      scored.push({ id: candidate.id, score, forced });
    }

    scored.sort((a, b) => b.score - a.score);
    const forcedIds = scored.filter((item) => item.forced).map(({ id }) => id);
    // Prioritize visible equipment in camera frustum (score > 0)
    const visibleRegularIds = scored
      .filter((item) => !item.forced && item.score > 0)
      .slice(0, Math.max(0, maxDetailed - forcedIds.length))
      .map(({ id }) => id);
    const target = [...forcedIds, ...visibleRegularIds];

    // Responsive promotions: 6 per tick when resting, 2 per tick while moving (forced promoted immediately)
    const maxPromote = isMoving || cameraDragStore.isDragging() ? 2 : 6;
    const next = stepEquipmentDetailIds(
      idsRef.current,
      target,
      options.forceIds,
      EQUIPMENT_DETAIL_GRADUAL_STEP,
      maxPromote
    );
    const key = next.slice().sort().join('|');
    if (key === keyRef.current) return;
    keyRef.current = key;
    previousRef.current = new Set(next);
    setIds(next);
  });

  return useMemo(() => new Set(ids), [ids]);
}

/** Height in CSS pixels that `worldSize` covers at this point in the scene. */
export function projectedPixelSize(
  camera: THREE.PerspectiveCamera,
  viewportHeight: number,
  worldX: number,
  worldY: number,
  worldZ: number,
  worldSize: number
): number {
  const dx = camera.position.x - worldX;
  const dy = camera.position.y - worldY;
  const dz = camera.position.z - worldZ;
  const dist = Math.hypot(dx, dy, dz);
  // Standing inside the machine counts as maximum detail rather than a divide
  // by zero that would collapse it to a box.
  if (dist < 1e-3) return Number.POSITIVE_INFINITY;
  const frustumHeight = 2 * dist * Math.tan((camera.fov * Math.PI) / 360);
  if (frustumHeight <= 0) return Number.POSITIVE_INFINITY;
  return (worldSize / frustumHeight) * viewportHeight;
}

export type EquipmentLodRootProps = {
  worldX: number;
  worldZ: number;
  /** Centre height of the machine — the projection is measured from here. */
  worldY?: number;
  /** Largest world dimension, projected to decide how big the machine looks. */
  worldSize: number;
  elementId: string;
  kind: 'stocker' | 'equipment';
  children: (lod: EquipmentLodLevel) => React.ReactNode;
};

/**
 * Debounced screen-size LOD. Updates React state at most ~5 Hz, never every frame.
 */
export const EquipmentLodRoot: React.FC<EquipmentLodRootProps> = ({
  worldX,
  worldZ,
  worldY = 0,
  worldSize,
  elementId,
  children,
}) => {
  const running = useAppSelector((s) => s.simulation.running);
  const [lod, setLod] = useState<EquipmentLodLevel>('near');
  const accumRef = useRef(0);
  const lodRef = useRef<EquipmentLodLevel>('near');

  // Camera and viewport come from the frame state so a large fab does not open
  // one store subscription per machine just to watch for resizes.
  useFrame((state, dt) => {
    accumRef.current += dt;
    if (accumRef.current < CHECK_INTERVAL_S) return;
    accumRef.current = 0;

    const apply = (next: EquipmentLodLevel) => {
      if (next === lodRef.current) return;
      lodRef.current = next;
      setLod(next);
    };

    if (running && stationHasActiveTransfer(elementId)) {
      apply('near');
      return;
    }

    const { camera, size } = state;

    if (camera instanceof THREE.PerspectiveCamera) {
      const px = projectedPixelSize(
        camera,
        size.height,
        worldX,
        worldY,
        worldZ,
        worldSize
      );
      apply(
        lodRef.current === 'near'
          ? px < FAR_PX
            ? 'far'
            : 'near'
          : px > NEAR_PX
            ? 'near'
            : 'far'
      );
      return;
    }

    const dx = camera.position.x - worldX;
    const dz = camera.position.z - worldZ;
    const dist = Math.hypot(dx, dz);
    apply(
      lodRef.current === 'near'
        ? dist > FALLBACK_FAR_DIST
          ? 'far'
          : 'near'
        : dist < FALLBACK_NEAR_DIST
          ? 'near'
          : 'far'
    );
  });

  return <>{children(lod)}</>;
};
